import { useEffect, useState } from 'react'
import { api } from '@/api'
import type { UsageSummary } from '@/api/types'
import { TemplateGallery } from '@/components/TemplateGallery'
import { Stat } from '@/components/ui'
import { useLibraryStats } from '@/hooks/useLibraryStats'

/**
 * 工作台首页 —— **2026-09-29 按用户要求收敛为「产能概览 + 模板中心」**。
 *
 * 用户原话：「首页改为模板中心为主，保留 [四张统计卡]，其他不需要」。
 *
 * 删掉的四块，以及它们为什么本来就该在别处：
 *  - **Hero 大标题 + 三个按钮**：「开始生成」跳的是 `/banner`（一个具体页面，
 *    凭什么代表全站入口），「一键热门同款」跳的就是现在的首页模板区（原地转圈）。
 *    真正需要入口的东西，模板墙本身就够 —— 20 张卡都是入口。
 *  - **快捷开始 12 项**：那是**侧栏的镜像**。侧栏已有的东西在首页再铺一遍，
 *    两份清单维护同一个 IA，加一条页面就要记得改两处（模板从 12 套变 20 套时
 *    就漏过）。用户找工具走侧栏，首页不该是第二份导航。
 *  - **最近任务 / 产出趋势 / 页面用量占比 / 最近作品**：这四块全是**账本**，
 *    已经有各自的页面（任务中心 / 用量与明细 / 我的作品），且有顶栏常驻入口。
 *    在首页再摊一遍，等于每个页面都要同步一次口径。
 *
 * 留下的统计卡是**唯一有存在理由的一块**：它回答的是「我今天干了多少、花了多少」，
 * 是开工前扫一眼的状态，而四个数字分别落在四个不同页面，不摆出来就得多点四次。
 * 四张卡不点、不展开、不做二级入口 —— 只看数，要看明细走顶栏。
 */
export function Dashboard() {
  /*
   * ⚠️ 这两张卡的数字**必须来自后端库**，不能来自浏览器镜像。
   *
   * 原来读的是 `useWorks().stats` / `useAssetLibrary().assets` —— 两个 Context
   * 里的 localStorage 镜像。用户清一次站点数据，首页就从「88 个作品」变成 0，
   * 而库里的行一条没少：看不到、但还在，这正是最容易被误解成「数据丢了」的形态。
   *
   * 读不到时显示「—」（不是 0）：0 会被读成「东西没了」，— 才是「没读到」。
   */
  const library = useLibraryStats()
  const [usage, setUsage] = useState<UsageSummary | null>(null)

  useEffect(() => {
    api.usage().then(setUsage).catch(() => setUsage(null))
  }, [])

  return (
    <div className="page page--wide">
      <div className="stat-grid">
        <Stat
          label="今日生成"
          value={usage ? usage.recent24h : '—'}
          delta={usage ? `近 14 天累计 ${usage.totalCalls.toLocaleString()} 次` : undefined}
          icon="activity"
          suffix="次"
        />
        <Stat
          label="本月消耗"
          value={usage ? `¥${usage.totalCost.toFixed(2)}` : '—'}
          delta={usage ? `成功率 ${(usage.successRate * 100).toFixed(1)}%` : undefined}
          deltaTone="up"
          icon="coin"
        />
        <Stat
          label="作品总数"
          value={library ? library.works : '—'}
          delta={library ? `其中收藏 ${library.favorites}` : '读取中…'}
          icon="folder"
        />
        <Stat
          label="素材库"
          value={library ? library.assets : '—'}
          delta="全站共用，可一键复用"
          icon="archive"
        />
      </div>

      <div className="section-gap" />

      <TemplateGallery />
    </div>
  )
}
